// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';
import { APP_HTML } from './template';
import { EventLog } from './eventlog';
import { initGlossary } from './glossary';
import {
  ABPlayer,
  canShareFiles,
  downloadBlob,
  initModals,
  saveBlob,
  shareFiles,
  toast,
  type PlayWhich,
} from './ui';
import { prepareFromFile, prepareFromRecording, releaseDecodeContext } from './prepare';
import { Denoiser, encodeAudio } from './processor';
import { MicRecorder } from './recorder';
import { drawWaveform, renderReportCard } from './report';
import { buildOutputName, clamp, formatDuration, mixDryWet } from './audio';
import type { DenoiseResult, OutputFormat, PreparedAudio } from './types';

const root = document.getElementById('app') as HTMLElement;
root.innerHTML = APP_HTML;

const log = new EventLog(root);
initModals(root);
initGlossary(root);

// ── State ────────────────────────────────────────────────────────────────
const denoiser = new Denoiser();
let recorder: MicRecorder | null = null;
let recTimer: number | undefined;
let prepared: PreparedAudio | null = null;
let result: DenoiseResult | null = null;
let currentMix: Float32Array | null = null;
let strength = 1;
let format: OutputFormat = 'wav';

const player = new ABPlayer((which) => reflectPlayState(which));

// ── View switching ─────────────────────────────────────────────────────────
type View = 'input' | 'record' | 'progress' | 'result' | 'error';
const panels: Record<View, HTMLElement> = {
  input: byId('input-panel'),
  record: byId('record-panel'),
  progress: byId('progress-panel'),
  result: byId('result-panel'),
  error: byId('error-box'),
};

function setView(view: View): void {
  (Object.keys(panels) as View[]).forEach((k) => (panels[k].hidden = k !== view));
  byId('hero').hidden = view !== 'input';
}

function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

// ── Input wiring ─────────────────────────────────────────────────────────
const fileInput = byId<HTMLInputElement>('file-input');
const dropzone = byId('dropzone');

byId('pick-btn').addEventListener('click', (e) => {
  e.stopPropagation();
  fileInput.click();
});
dropzone.addEventListener('click', () => fileInput.click());
dropzone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fileInput.click();
  }
});
fileInput.addEventListener('change', () => {
  const f = fileInput.files?.[0];
  if (f) void handleFile(f);
  fileInput.value = '';
});

['dragenter', 'dragover'].forEach((ev) =>
  dropzone.addEventListener(ev, (e) => {
    e.preventDefault();
    dropzone.classList.add('drag');
  }),
);
['dragleave', 'drop'].forEach((ev) =>
  dropzone.addEventListener(ev, (e) => {
    e.preventDefault();
    if (ev === 'dragleave' && dropzone.contains(e.target as Node)) return;
    dropzone.classList.remove('drag');
  }),
);
dropzone.addEventListener('drop', (e) => {
  const f = (e as DragEvent).dataTransfer?.files?.[0];
  if (f) void handleFile(f);
});

byId('sample-btn').addEventListener('click', (e) => {
  e.stopPropagation();
  void loadSample();
});

async function loadSample(): Promise<void> {
  try {
    log.add('Loading sample clip…', 'info');
    const base = (import.meta as unknown as { env: { BASE_URL: string } }).env.BASE_URL || '/';
    const res = await fetch(`${base}samples/noisy-voice.wav`);
    if (!res.ok) throw new Error(`Sample not found (${res.status})`);
    const blob = await res.blob();
    const file = new File([blob], 'sample-noisy-voice.wav', { type: 'audio/wav' });
    await handleFile(file);
  } catch (err) {
    showError(err);
  }
}

// ── Recording ────────────────────────────────────────────────────────────
byId('record-btn').addEventListener('click', (e) => {
  e.stopPropagation();
  void startRecording();
});
byId('rec-stop').addEventListener('click', () => void stopRecording());
byId('rec-cancel').addEventListener('click', () => cancelRecording());

async function startRecording(): Promise<void> {
  if (!MicRecorder.isSupported()) {
    toast('Recording is not supported here — drop a file instead.');
    return;
  }
  recorder = new MicRecorder();
  try {
    await recorder.start((peak) => {
      byId('level-fill').style.width = `${clamp(peak, 0, 1) * 100}%`;
    });
  } catch (err) {
    recorder = null;
    log.add('Microphone permission denied.', 'warn');
    toast('No mic access — you can still drop or pick a file.');
    return;
  }
  log.add('Recording started (nothing is saved yet).', 'good');
  setView('record');
  updateRecTime();
  recTimer = window.setInterval(updateRecTime, 250);
}

function updateRecTime(): void {
  if (!recorder) return;
  byId('rec-time').textContent = formatDuration(recorder.elapsed());
}

async function stopRecording(): Promise<void> {
  if (!recorder) return;
  window.clearInterval(recTimer);
  const rec = await recorder.stop();
  recorder = null;
  if (rec.samples.length < 480) {
    log.add('Recording too short.', 'warn');
    toast('That was too short — try again.');
    setView('input');
    return;
  }
  log.add(`Recorded ${formatDuration(rec.samples.length / rec.sampleRate)} of audio.`, 'good');
  const prep = await prepareFromRecording(rec.samples, rec.sampleRate, 'recording');
  await runDenoise(prep);
}

function cancelRecording(): void {
  window.clearInterval(recTimer);
  recorder?.teardown();
  recorder = null;
  log.add('Recording cancelled.', 'info');
  setView('input');
}

// ── The core pipeline ──────────────────────────────────────────────────────
async function handleFile(file: File): Promise<void> {
  try {
    player.stop();
    setView('progress');
    setProgress(0, 'Reading file…', `${file.name}`);
    log.add(`Loading “${file.name}” (${(file.size / 1024 / 1024).toFixed(1)} MB)`, 'info');
    const prep = await prepareFromFile(file);
    log.add(
      `Decoded ${formatDuration(prep.duration)} · mono · 48 kHz`,
      'good',
    );
    await runDenoise(prep);
  } catch (err) {
    showError(err);
  }
}

async function runDenoise(prep: PreparedAudio): Promise<void> {
  prepared = prep;
  setView('progress');
  setProgress(2, 'Loading the noise-suppression model…', 'RNNoise · runs on your device');
  const t0 = performance.now();
  try {
    const res = await denoiser.denoise(prep, (frac) => {
      const pct = Math.round(2 + frac * 96);
      const secs = prep.duration * frac;
      setProgress(
        pct,
        'Cleaning…',
        `${formatDuration(secs)} of ${formatDuration(prep.duration)} processed`,
      );
    });
    const elapsed = (performance.now() - t0) / 1000;
    const speed = elapsed > 0 ? prep.duration / elapsed : 0;
    log.add(
      `Cleaned in ${elapsed.toFixed(1)} s (${speed.toFixed(1)}× realtime).`,
      'good',
    );
    result = res;
    showResult(res);
  } catch (err) {
    showError(err);
  }
}

function setProgress(pct: number, label: string, sub: string): void {
  byId('progress-fill').style.width = `${clamp(pct, 0, 100)}%`;
  byId('progress-pct').textContent = `${Math.round(pct)}%`;
  byId('progress-label').textContent = label;
  byId('progress-sub').textContent = sub;
}

byId('cancel-btn').addEventListener('click', () => {
  denoiser.terminate();
  log.add('Cancelled.', 'warn');
  setView('input');
});

// ── Result ─────────────────────────────────────────────────────────────────
function showResult(res: DenoiseResult): void {
  const reduction = Math.max(0, res.noiseFloorInDb - res.noiseFloorOutDb);
  byId('reduction-num').textContent = `−${reduction.toFixed(reduction < 10 ? 1 : 0)}`;
  byId('floor-detail').textContent =
    `${res.noiseFloorInDb.toFixed(0)} → ${res.noiseFloorOutDb.toFixed(0)} dBFS`;

  strength = 1;
  const slider = byId<HTMLInputElement>('strength-slider');
  slider.value = '100';
  byId('strength-val').textContent = '100%';

  player.setDry(res.dry, res.sampleRate);
  applyStrength(); // sets currentMix + wet buffer + wet waveform
  drawWaveform(byId<HTMLCanvasElement>('wave-dry'), res.dry, '#5b6b64');

  byId('share-btn').hidden = !canShareFiles([new File([new Uint8Array(1)], 'a.wav')]);

  setView('result');
  log.add(`Background noise reduced by ${reduction.toFixed(1)} dB.`, 'good');
}

function applyStrength(): void {
  if (!result) return;
  currentMix = mixDryWet(result.dry, result.wet, strength);
  player.setWet(currentMix);
  drawWaveform(byId<HTMLCanvasElement>('wave-wet'), currentMix, '#34e6bf');
}

byId<HTMLInputElement>('strength-slider').addEventListener('input', (e) => {
  strength = parseInt((e.target as HTMLInputElement).value, 10) / 100;
  byId('strength-val').textContent = `${Math.round(strength * 100)}%`;
});
byId<HTMLInputElement>('strength-slider').addEventListener('change', () => {
  const wasPlaying = player.isPlaying() === 'wet';
  applyStrength();
  if (wasPlaying) void player.play('wet');
});

// A/B play buttons
byId('play-dry').addEventListener('click', () => void player.toggle('dry'));
byId('play-wet').addEventListener('click', () => void player.toggle('wet'));

function reflectPlayState(which: PlayWhich | null): void {
  for (const [id, w] of [['play-dry', 'dry'], ['play-wet', 'wet']] as const) {
    const btn = byId(id);
    const active = which === w;
    btn.classList.toggle('playing', active);
    (btn.querySelector('.ic-play') as HTMLElement).hidden = active;
    (btn.querySelector('.ic-pause') as HTMLElement).hidden = !active;
  }
}

// Space toggles the cleaned preview when the result is showing.
document.addEventListener('keydown', (e) => {
  if (e.key === ' ' && !panels.result.hidden) {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'TEXTAREA') return;
    e.preventDefault();
    void player.toggle('wet');
  }
});

// Format toggle
root.querySelectorAll<HTMLElement>('.fmt').forEach((btn) => {
  btn.addEventListener('click', () => {
    root.querySelectorAll('.fmt').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    format = (btn.dataset.fmt as OutputFormat) || 'wav';
  });
});

// ── Output actions ─────────────────────────────────────────────────────────
async function encodeCurrent(): Promise<{ blob: Blob; name: string }> {
  if (!currentMix || !result || !prepared) throw new Error('Nothing to export');
  const btn = byId<HTMLButtonElement>('download-btn');
  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Encoding…';
  try {
    const { blob } = await encodeAudio(currentMix, result.sampleRate, format, () => {});
    const name = buildOutputName(prepared.sourceName, format);
    return { blob, name };
  } finally {
    btn.disabled = false;
    btn.textContent = label;
  }
}

byId('download-btn').addEventListener('click', async () => {
  try {
    const { blob, name } = await encodeCurrent();
    const mime = format === 'wav' ? 'audio/wav' : 'audio/mpeg';
    const r = await saveBlob(blob, name, `${format.toUpperCase()} audio`, `.${format}`, mime);
    if (r !== 'cancelled') log.add(`Exported ${name}`, 'good');
    if (r === 'downloaded') toast('Downloaded');
  } catch (err) {
    showError(err);
  }
});

byId('share-btn').addEventListener('click', async () => {
  try {
    const { blob, name } = await encodeCurrent();
    const file = new File([blob], name, { type: blob.type });
    if (canShareFiles([file])) {
      const ok = await shareFiles([file], 'Cleaned audio — Hushwell', 'Denoised with hushwell.benrichardson.dev');
      if (ok) log.add('Shared cleaned audio.', 'good');
    } else {
      downloadBlob(blob, name);
      toast('Sharing unavailable — downloaded instead');
    }
  } catch (err) {
    showError(err);
  }
});

byId('report-btn').addEventListener('click', async () => {
  try {
    if (!result || !prepared) return;
    const blob = await renderReportCard(result, prepared.sourceName);
    const r = await saveBlob(blob, 'hushwell-report.png', 'PNG image', '.png', 'image/png');
    if (r !== 'cancelled') log.add('Saved report image.', 'good');
    if (r === 'downloaded') toast('Report image downloaded');
  } catch (err) {
    showError(err);
  }
});

byId('copy-btn').addEventListener('click', async () => {
  if (!result) return;
  const reduction = Math.max(0, result.noiseFloorInDb - result.noiseFloorOutDb);
  const text =
    `Cleaned a ${formatDuration(result.dry.length / result.sampleRate)} voice recording with Hushwell — ` +
    `background noise reduced by ${reduction.toFixed(1)} dB, entirely on-device. hushwell.benrichardson.dev`;
  try {
    await navigator.clipboard.writeText(text);
    toast('Summary copied');
  } catch {
    toast('Could not copy');
  }
});

byId('again-btn').addEventListener('click', () => reset());
byId('error-retry').addEventListener('click', () => reset());

function reset(): void {
  player.stop();
  denoiser.terminate();
  prepared = null;
  result = null;
  currentMix = null;
  setView('input');
}

function showError(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  byId('error-text').textContent =
    message.length > 160 ? message.slice(0, 159) + '…' : message || 'Something went wrong.';
  log.add(`Error: ${message}`, 'bad');
  setView('error');
}

// ── Teardown ─────────────────────────────────────────────────────────────
window.addEventListener('pagehide', () => {
  recorder?.teardown();
  player.dispose();
  denoiser.terminate();
  releaseDecodeContext();
});

// ── PWA ─────────────────────────────────────────────────────────────────
registerSW();
async function registerSW(): Promise<void> {
  try {
    const { registerSW } = await import('virtual:pwa-register');
    registerSW({ immediate: true });
  } catch {
    /* dev mode without the plugin virtual module */
  }
}

// ── Test hook (used by the Step-11 dry-run; harmless in production) ──────────
(window as unknown as { __hushwell: unknown }).__hushwell = {
  async runSynthetic(samples: Float32Array, sampleRate = 48000): Promise<DenoiseResult> {
    const prep = await prepareFromRecording(samples, sampleRate, 'synthetic');
    return denoiser.denoise(prep, () => {});
  },
  state: () => ({ hasResult: !!result, strength, format }),
};

setView('input');
log.add('Ready. Everything runs on your device.', 'good');
